import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import icons from './shared/Icons';
import Section from './shared/Section';
import SectionHeading from './shared/SectionHeading';

/* ── All features for comparison ── */
const allFeatures = [
  'Users', 'Data processing', 'Connectors', 'Support', 'Dashboards',
  'Reporting', 'API access', 'Custom models', 'On-premise', 'SLA guarantee',
  'White-label', 'Dedicated CSM',
];

const plans = [
  {
    name: 'Starter',
    monthly: 79,
    annual: 63,
    desc: 'Perfect for small teams getting started with AI analytics.',
    features: {
      'Users': 'Up to 5',
      'Data processing': '10 GB',
      'Connectors': '50+',
      'Support': 'Email',
      'Dashboards': 'Basic',
      'Reporting': 'Weekly',
      'API access': '—',
      'Custom models': '—',
      'On-premise': '—',
      'SLA guarantee': '—',
      'White-label': '—',
      'Dedicated CSM': '—',
    },
    cta: 'Start Free Trial',
    popular: false,
    offset: 24,
  },
  {
    name: 'Professional',
    monthly: 199,
    annual: 159,
    desc: 'For growing organizations that need advanced capabilities.',
    features: {
      'Users': 'Up to 25',
      'Data processing': '100 GB',
      'Connectors': '150+',
      'Support': 'Priority (4h SLA)',
      'Dashboards': 'Custom',
      'Reporting': 'Real-time',
      'API access': '✓',
      'Custom models': '✓',
      'On-premise': '—',
      'SLA guarantee': '—',
      'White-label': '—',
      'Dedicated CSM': '—',
    },
    cta: 'Start Free Trial',
    popular: true,
    offset: 0,
  },
  {
    name: 'Enterprise',
    monthly: 399,
    annual: 319,
    desc: 'Tailored solutions for large-scale deployments.',
    features: {
      'Users': 'Unlimited',
      'Data processing': 'Unlimited',
      'Connectors': '200+',
      'Support': '24/7 Phone',
      'Dashboards': 'Custom + embedded',
      'Reporting': 'Real-time + NLP',
      'API access': '✓',
      'Custom models': '✓',
      'On-premise': '✓',
      'SLA guarantee': '99.99%',
      'White-label': '✓',
      'Dedicated CSM': '✓',
    },
    cta: 'Contact Sales',
    popular: false,
    offset: 24,
  },
];

/* ── PayPal checkout button ── */
const PlanPayPalButton = ({ price, planName, annual, popular }) => {
  const [{ isPending }] = usePayPalScriptReducer();
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  if (success) {
    return (
      <div className="w-full py-3.5 rounded-xl font-semibold text-center text-black" style={{ background: 'linear-gradient(135deg, #08CB00, #54E346)' }}>
        ✓ Payment Successful!
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="w-full py-3.5 rounded-xl font-semibold text-center border border-primary/30 text-primary animate-pulse">
        Loading PayPal…
      </div>
    );
  }

  return (
    <div>
      {errorMsg && (
        <p className="text-red-400 text-xs text-center mb-2">{errorMsg}</p>
      )}
      <PayPalButtons
        style={{
          layout: 'vertical',
          shape: 'rect',
          color: popular ? 'gold' : 'blue',
          label: 'pay',
          height: 45,
        }}
        forceReRender={[price, planName, annual]}
        createOrder={(_data, actions) =>
          actions.order.create({
            purchase_units: [{
              description: `Opticvance ${planName} Plan – ${annual ? 'Annual' : 'Monthly'}`,
              amount: {
                value: price.toString(),
                currency_code: 'USD',
              },
            }],
          })
        }
        onApprove={async (_data, actions) => {
          await actions.order.capture();
          setSuccess(true);
          setErrorMsg(null);
        }}
        onError={() => setErrorMsg('Payment failed. Please try again.')}
        onCancel={() => setErrorMsg('Payment cancelled.')}
      />
    </div>
  );
};

/* ── Toggle switch ── */
const BillingToggle = ({ annual, setAnnual }) => (
  <div className="flex items-center justify-center gap-4 mb-14">
    <span className={`text-sm font-medium transition-colors ${!annual ? 'text-white' : 'text-gray-500'}`}>Monthly</span>
    <button
      onClick={() => setAnnual((a) => !a)}
      className="relative w-14 h-7 rounded-full bg-dark-card border border-dark-border transition-colors focus:outline-none"
      role="switch"
      aria-checked={annual}
      aria-label="Toggle annual billing"
    >
      <motion.div
        className="absolute top-0.5 w-6 h-6 rounded-full"
        style={{ background: 'linear-gradient(135deg, #08CB00, #54E346)' }}
        animate={{ left: annual ? 28 : 2 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      />
    </button>
    <span className={`text-sm font-medium transition-colors ${annual ? 'text-white' : 'text-gray-500'}`}>
      Annual <span className="text-primary text-xs font-mono ml-1">Save 17%</span>
    </span>
  </div>
);

/* ── Price display with animation ── */
const AnimatedPrice = ({ plan, annual }) => {
  if (plan.monthly === null) {
    return <span className="text-4xl font-bold gradient-text">Custom</span>;
  }
  const price = annual ? plan.annual : plan.monthly;
  return (
    <span className="inline-flex items-baseline">
      <span className="text-lg text-gray-500 mr-1">$</span>
      <AnimatePresence mode="wait">
        <motion.span
          key={price}
          className="text-4xl font-bold gradient-text"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 20, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {price}
        </motion.span>
      </AnimatePresence>
      <span className="text-gray-500 ml-1 text-sm">/mo</span>
    </span>
  );
};

/* ── Pricing card ── */
const PricingCard = ({ plan, annual, onDemo, index }) => (
  <motion.div
    className={`glass-card rounded-2xl p-8 relative flex flex-col ${
      plan.popular ? 'border-primary/50 z-10' : ''
    }`}
    style={{ marginTop: window.innerWidth >= 768 ? plan.offset : 0 }}
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.15 }}
    whileHover={{ y: -8, transition: { duration: 0.3 } }}
  >
    {plan.popular && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-black" style={{ background: 'linear-gradient(135deg, #08CB00, #54E346)' }}>
        Most Popular
      </div>
    )}

    <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
    <div className="mb-4 h-12 flex items-end">
      <AnimatedPrice plan={plan} annual={annual} />
    </div>
    <p className="text-gray-400 text-sm mb-6">{plan.desc}</p>

    {/* Key features (top 6) */}
    <ul className="space-y-3 mb-8 flex-1">
      {Object.entries(plan.features).slice(0, 6).map(([key, val]) => {
        const hasCheck = val === '✓' || (val !== '—' && val !== '');
        return (
          <li key={key} className="flex items-center gap-2 text-sm text-gray-300">
            <span className={`flex-shrink-0 ${hasCheck ? 'text-secondary' : 'text-gray-600'}`}>
              {hasCheck ? icons.check : <span className="w-5 h-5 block text-center">—</span>}
            </span>
            <span>{val === '✓' ? key : `${val} ${val === '—' ? '' : key.toLowerCase()}`}</span>
          </li>
        );
      })}
    </ul>

    {plan.monthly === null ? (
      /* Enterprise – Contact Sales */
      <button
        onClick={onDemo}
        className="w-full py-3.5 rounded-xl font-semibold transition-all duration-300 border border-primary/30 text-primary hover:bg-primary/10"
      >
        {plan.cta}
      </button>
    ) : (
      /* Starter & Professional – PayPal checkout */
      <PlanPayPalButton
        price={annual ? plan.annual : plan.monthly}
        planName={plan.name}
        annual={annual}
        popular={plan.popular}
      />
    )}
  </motion.div>
);

/* ── Comparison table ── */
const ComparisonTable = () => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 30 }}
    transition={{ duration: 0.4 }}
    className="mt-12"
  >
    <div className="overflow-x-auto pb-4 -mx-4 px-4 lg:mx-0 lg:px-0" style={{ scrollbarWidth: 'thin' }}>
      <table className="w-full min-w-[640px]">
        <thead>
          <tr className="border-b border-dark-border/50">
            <th className="text-left py-3 pr-4 text-sm text-gray-500 font-medium w-1/4">Feature</th>
            {plans.map((p) => (
              <th key={p.name} className={`text-center py-3 px-4 text-sm font-semibold ${p.popular ? 'text-primary' : 'text-white'}`}>
                {p.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {allFeatures.map((feat, i) => (
            <tr key={feat} className="border-b border-dark-border/20">
              <td className="py-3 pr-4 text-sm text-gray-400">{feat}</td>
              {plans.map((p) => {
                const val = p.features[feat];
                const isCheck = val === '✓';
                const isDash = val === '—';
                return (
                  <td key={p.name} className="text-center py-3 px-4 text-sm">
                    {isCheck ? (
                      <span className="inline-flex text-secondary justify-center">{icons.check}</span>
                    ) : isDash ? (
                      <span className="text-gray-600">—</span>
                    ) : (
                      <span className="text-gray-300">{val}</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </motion.div>
);

/* ── Main ── */
const Pricing = ({ onDemo }) => {
  const [annual, setAnnual] = useState(false);
  const [showComparison, setShowComparison] = useState(false);

  return (
    <Section id="pricing" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          tag="Pricing"
          title={<>Transparent Plans, <span className="gradient-text">Exceptional Value</span></>}
          desc="Start free. Scale as you grow. No hidden fees. Cancel anytime."
        />

        <BillingToggle annual={annual} setAnnual={setAnnual} />

        {/* Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 items-start">
          {plans.map((plan, i) => (
            <PricingCard key={plan.name} plan={plan} annual={annual} onDemo={onDemo} index={i} />
          ))}
        </div>

        {/* Comparison toggle */}
        <div className="text-center mt-12">
          <button
            onClick={() => setShowComparison((s) => !s)}
            className="inline-flex items-center gap-2 text-sm text-primary/70 hover:text-primary transition-colors"
          >
            <span>{showComparison ? 'Hide' : 'Compare all'} features</span>
            <motion.svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4" animate={{ rotate: showComparison ? 180 : 0 }}>
              <path d="M6 9l6 6 6-6" />
            </motion.svg>
          </button>
        </div>

        <AnimatePresence>
          {showComparison && <ComparisonTable />}
        </AnimatePresence>
      </div>
    </Section>
  );
};

export default Pricing;
